import { UnreadsProvider, UserInfoProvider } from '@repo/core'
import { dbInstance } from '@repo/db/src/instances/web'
import { RestChatService, StompProvider } from '@repo/network'
import { QueryClientProvider } from '@tanstack/react-query'
import { SnackbarProvider } from '@tecsinapse/cortex-react'
import { enableAnalytics } from '@web/analytics'
import { NotificationLayer } from '@web/notifications/NotificationLayer'
import { enableUserguiding } from '@web/userguiding'
import Keycloak from 'keycloak-js'
import React, { useEffect, useState } from 'react'
import './index.css'
import {
  client,
  ErrorBoundary,
  GlobalLoading,
  KeycloakProvider,
  KeycloakUserInfo,
  PiPProvider,
  read,
  store,
  UPDATE_USER
} from './modules/core'
import { Router } from './modules/core/router'
import { KeycloakVO } from '@repo/types'

export interface ChatInitProps {
  userGuiding: boolean
  updateUserDestinations: boolean
  connectionKey: string | undefined
  clientCode: string | undefined
  phoneNumber: string | undefined
}

interface AppProps {
  kcInfo: KeycloakVO
  kcInstance: Keycloak
  chatInitConfig: ChatInitProps
  currentUser: KeycloakUserInfo
}

const App = ({ kcInfo, kcInstance, chatInitConfig, currentUser }: AppProps) => {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (read(UPDATE_USER)) {
      RestChatService.updateUser({
        userId: currentUser.sub,
        userName: currentUser.name
      })
        .catch(() => console.error('User cannot be updated'))
        .finally(() => {
          store(UPDATE_USER, false)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [currentUser])

  useEffect(() => {
    if (!loading) {
      enableAnalytics(kcInstance).then()
      enableUserguiding(chatInitConfig, currentUser).then()
    }
  }, [kcInstance, chatInitConfig, currentUser, loading])

  if (loading) return <GlobalLoading />

  return (
    <ErrorBoundary>
      <KeycloakProvider
        info={kcInfo}
        instance={kcInstance}
      >
        <SnackbarProvider options={{ position: 'bottom-center' }}>
          <StompProvider>
            <QueryClientProvider client={client}>
              <UnreadsProvider dbInstance={dbInstance}>
                <UserInfoProvider>
                  <PiPProvider>
                    <Router />
                    <NotificationLayer />
                  </PiPProvider>
                </UserInfoProvider>
              </UnreadsProvider>
            </QueryClientProvider>
          </StompProvider>
        </SnackbarProvider>
      </KeycloakProvider>
    </ErrorBoundary>
  )
}

export default App
